import { useEffect, useState } from "react";
import { BookOpen, Download, ExternalLink, FileText } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { toast } from "sonner";

export default function Manual() {
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/manual/info").then(({ data }) => setInfo(data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const filename = info?.filename || "LabTrack_User_Manual.pdf";

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/manual", { params: { download: true }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success(`${filename} downloaded`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="manual-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">
            User Manual
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            A complete, illustrated guide to every section of LabTrack — computer and phone views
            side by side, with flow charts and numbered figures. Landscape PDF, always generated
            from the live application.
          </p>
          {info && (
            <p className="text-xs text-[#6B7280] mt-2" data-testid="manual-meta">
              Version {info.version} · {info.date} · support {info.support_email}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="manual-preview-btn">
            <FileText className="h-4 w-4 mr-2 text-[#C92A2A]" /> Preview
          </Button>
          <Button onClick={download} disabled={busy} data-testid="manual-download-btn"
            className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            <Download className="h-4 w-4 mr-2" /> {busy ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <div className="rounded-md border border-[#E5E7EB] bg-white p-4 lg:col-span-2">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-[#002FA7]" />
            <h2 className="font-heading font-bold text-sm text-[#111827]">What is inside</h2>
          </div>
          <ol className="mt-3 grid gap-1.5 sm:grid-cols-2 text-sm text-[#374151]"
            data-testid="manual-chapter-list">
            {(info?.chapters || []).map((c) => (
              <li key={c.number}>{c.number}. {c.title}</li>
            ))}
          </ol>
          {!info && <p className="text-sm text-[#6B7280] mt-3">Loading chapters…</p>}
        </div>

        <div className="rounded-md border border-[#E5E7EB] bg-white p-4">
          <h2 className="font-heading font-bold text-sm text-[#111827]">How to use it</h2>
          <ul className="mt-3 space-y-2 text-sm text-[#374151]">
            <li>The contents page is clickable — jump straight to a section.</li>
            <li>Red boxes on the screenshots mark the exact control being described.</li>
            <li>Every figure is numbered (Fig 4.1, 4.2…) and referenced in the text.</li>
            <li>Share it with new staff before their first shift, or with a lab evaluating LabTrack.</li>
          </ul>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/manual`} target="_blank" rel="noreferrer"
            data-testid="manual-newtab-link"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#002FA7] hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> Open in a new tab
          </a>
        </div>
      </div>

      <PdfPreviewDialog open={preview} onClose={() => setPreview(false)}
        path={preview ? "/manual" : null} filename={filename}
        title="LabTrack — User Manual" />
    </div>
  );
}
